import * as React from 'react';
import { cn } from '../../lib/utils';

export type TenantTier = 'sira' | 'ssp' | 'ssu' | 'site';

export interface TenantBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tier: TenantTier;
  name: string;
}

/**
 * Tenant scope chip — makes whose-data-is-on-screen impossible to lose.
 *
 * Hairline-bordered, monospace, with a leading dot whose color encodes the
 * tier. Used in headers, tables, alarm cards, and the active-incident rail.
 */
function TenantBadge({ tier, name, className, ...props }: TenantBadgeProps): React.JSX.Element {
  const meta = TIER_META[tier];
  return (
    <span
      title={meta.title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm border border-[var(--color-hairline-strong)]',
        'bg-[var(--color-surface)] px-1.5 py-0.5',
        'font-mono text-[0.6875rem] uppercase tracking-[0.14em] tabular-nums leading-none',
        'text-[var(--color-foreground-2)]',
        className,
      )}
      {...props}
    >
      <span aria-hidden className={cn('inline-block size-1.5 rounded-full', meta.dot)} />
      <span className="text-[var(--color-foreground-mute)]">{meta.tag}</span>
      <span className="text-[var(--color-foreground)] truncate">{name}</span>
    </span>
  );
}

const TIER_META: Record<TenantTier, { tag: string; dot: string; title: string }> = {
  sira: {
    tag: 'SIRA',
    dot: 'bg-[var(--color-amber-500)]',
    title: 'SIRA admin scope',
  },
  ssp: {
    tag: 'SSP',
    dot: 'bg-[var(--color-graphite-500)]',
    title: 'Security Service Provider',
  },
  ssu: {
    tag: 'SSU',
    dot: 'bg-[var(--color-graphite-400)]',
    title: 'Security System User',
  },
  site: {
    tag: 'SITE',
    dot: 'bg-[var(--color-graphite-300)]',
    title: 'Site',
  },
};

export { TenantBadge };
